import { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import parse from 'html-react-parser';
import {
  Ruler,
  Weight,
  Calendar,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useAuth } from '@/features/auth';
import { supabase } from '@/lib/supabase';
import { SSWCircle, BabyNameEntry } from '@/components/SSWCircle';
import { RecommendationSlot } from '@/features/recommendations';

type UserRole = 'mother' | 'father' | 'partner' | null;

type ParentNames = {
  user1Name: string | null;
  user2Name: string | null;
};

type OverviewSection = {
  title: string | null;
  content: string;
};

type SSWContent = {
  id: string;
  week_number: number;
  title: string;
  baby_development: string | string[];
  baby_size_cm: number | null;
  baby_weight_grams: number | null;
  baby_comparison: string | null;
  mother_body: string | string[];
  tips: string[] | null;
  partner_tips: string | null;
  overview_sections: OverviewSection[] | null;
  is_published: boolean;
};

// Helper to convert database value to array
function toArray(value: string | string[] | null | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        if (parsed.length === 1 && typeof parsed[0] === 'string' && parsed[0].startsWith('[')) {
          try {
            const inner = JSON.parse(parsed[0]);
            if (Array.isArray(inner)) return inner;
          } catch {
            // use outer
          }
        }
        return parsed;
      }
    } catch {
      // fall through
    }
  }
  return value.split('\n').filter((line) => line.trim() !== '');
}

// Get trimester from SSW
function getTrimester(ssw: number): number {
  if (ssw <= 12) return 1;
  if (ssw <= 27) return 2;
  if (ssw <= 40) return 3;
  return 4;
}

// Format weight display
function formatWeightDisplay(weightGrams: number | null, locale: string): { value: string; unit: string } | null {
  if (weightGrams === null || weightGrams === 0) return null;

  if (weightGrams < 1000) {
    return { value: `${weightGrams}`, unit: 'g' };
  } else {
    const kgValue = (weightGrams / 1000).toFixed(1);
    return { value: locale === 'de' ? kgValue.replace('.', ',') : kgValue, unit: 'kg' };
  }
}

// Helper to parse baby_names
const parseBabyNames = (names: unknown): BabyNameEntry[] => {
  if (!names || !Array.isArray(names) || names.length === 0) return [];
  if (typeof names[0] === 'string') {
    return names.map((name: string) => ({ name, gender: 'neutral' as const }));
  }
  return names as BabyNameEntry[];
};

export function SSWPage() {
  const { t, i18n } = useTranslation(['ssw', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentPregnancy, currentSSW, appMode, lastCompletedPregnancy } = usePregnancyStore();
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [sswContent, setSSWContent] = useState<SSWContent | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [parentNames, setParentNames] = useState<ParentNames>({ user1Name: null, user2Name: null });
  const [appointments, setAppointments] = useState<Array<{
    id: string;
    title: string;
    appointment_date: string;
    appointment_time: string | null;
  }>>([]);

  // Redirect to /baby in baby mode (SSW tracker not relevant after birth)
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  useEffect(() => {
    if (isBabyMode) {
      navigate('/baby', { replace: true });
    }
  }, [isBabyMode, navigate]);

  // Load SSW content from database (locale-aware with fallback to DE)
  useEffect(() => {
    async function loadSSWContent() {
      if (!currentSSW) return;

      setIsLoadingContent(true);

      // Try user's language first
      const { data } = await supabase
        .from('ssw_content')
        .select('*')
        .eq('week_number', currentSSW)
        .eq('locale', i18n.language)
        .eq('is_published', true)
        .maybeSingle();

      if (data) {
        setSSWContent(data);
      } else if (i18n.language !== 'de') {
        // Fallback to German
        const { data: deFallback } = await supabase
          .from('ssw_content')
          .select('*')
          .eq('week_number', currentSSW)
          .eq('locale', 'de')
          .eq('is_published', true)
          .maybeSingle();

        setSSWContent(deFallback);
      } else {
        setSSWContent(null);
      }

      setIsLoadingContent(false);
    }

    loadSSWContent();
  }, [currentSSW, i18n.language]);

  // Load user role, parent names and appointments
  useEffect(() => {
    async function loadData() {
      if (!user?.id || !currentPregnancy) return;

      // Get user role
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_role')
        .eq('id', user.id)
        .maybeSingle();

      if (profile?.user_role) {
        setUserRole(profile.user_role as UserRole);
      }

      // Get parent names via RPC function (bypasses RLS issues)
      const { data: parentNamesData, error: parentNamesError } = await supabase
        .rpc('get_ssw_parent_names', { p_pregnancy_id: currentPregnancy.id });

      console.log('SSW parent names:', { parentNamesData, parentNamesError });

      if (parentNamesData && !parentNamesData.error) {
        setParentNames({
          user1Name: parentNamesData.motherName || null,
          user2Name: parentNamesData.partnerName || null,
        });
      }

      // Get appointments for current week
      if (currentSSW) {
        const startOfWeek = getSSWStartDate(currentPregnancy.expected_due_date, currentSSW);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);

        const { data: appts } = await supabase
          .from('user_appointments')
          .select('id, title, appointment_date, appointment_time')
          .eq('pregnancy_id', currentPregnancy.id)
          .gte('appointment_date', startOfWeek.toISOString().split('T')[0])
          .lte('appointment_date', endOfWeek.toISOString().split('T')[0])
          .order('appointment_date', { ascending: true });

        if (appts) setAppointments(appts);
      }
    }

    loadData();
  }, [user?.id, currentPregnancy, currentSSW]);

  const dueDate = currentPregnancy ? new Date(currentPregnancy.expected_due_date) : null;
  const daysRemaining = dueDate
    ? Math.max(0, Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;
  const trimester = currentSSW ? getTrimester(currentSSW) : 1;

  // Calculate current day within the week (0-6)
  const dayInWeek = useMemo(() => {
    if (!currentPregnancy) return 0;
    const due = new Date(currentPregnancy.expected_due_date);
    const now = new Date();
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const totalDays = 280 - diffDays;
    return totalDays % 7;
  }, [currentPregnancy]);

  // Parse baby names
  const babyNames = parseBabyNames(currentPregnancy?.baby_names);

  // Body section title based on role
  const bodyTitle = useMemo(() => {
    if (userRole === 'father' || userRole === 'partner') {
      return t('ssw:content.bodyTitlePartner');
    }
    return t('ssw:content.bodyTitle');
  }, [userRole, t]);

  // Tips title based on partner connection
  const partnerTipsTitle = useMemo(() => {
    if (parentNames.user2Name) {
      return t('ssw:content.weeklyOverviewPartner');
    }
    return t('ssw:content.weeklyOverviewSingle');
  }, [parentNames.user2Name, t]);

  // Prepare display data
  const babyDevelopment = sswContent ? toArray(sswContent.baby_development) : [];
  const motherBody = sswContent ? toArray(sswContent.mother_body) : [];
  const tips = sswContent?.tips || [];

  // Parse overview sections (with fallback to partner_tips)
  const overviewSections: OverviewSection[] = useMemo(() => {
    const raw = sswContent?.overview_sections;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.filter((s) => s.content && s.content !== '<p></p>');
    }
    // Fallback: use partner_tips as single section
    if (sswContent?.partner_tips) {
      return [{ title: null, content: sswContent.partner_tips }];
    }
    return [];
  }, [sswContent]);

  // Size and weight formatting
  const weightData = formatWeightDisplay(sswContent?.baby_weight_grams ?? null, i18n.language);

  // Format date for display
  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';
  const formatDate = (date: Date) => {
    return date.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Build subtitle text (must be before early return to maintain hooks order)
  const subtitleText = useMemo(() => {
    if (!parentNames.user1Name) return null;
    if (parentNames.user2Name) {
      return t('ssw:subtitleWithPartner', { name1: parentNames.user1Name, name2: parentNames.user2Name });
    }
    return t('ssw:subtitle', { name: parentNames.user1Name });
  }, [parentNames, t]);

  if (!currentSSW) {
    return (
      <div className="text-center text-white py-12">
        <p className="text-white/70">{t('ssw:noData')}</p>
        <Link to="/" className="text-pp-orange mt-2 inline-block">
          {t('ssw:backToDashboard')}
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Page Title */}
      <section className="pb-6">
        <h1 className="text-white text-2xl font-bold">{t('ssw:pageTitle')}</h1>
        {subtitleText && (
          <p className="text-white/70 text-base">{subtitleText}</p>
        )}
      </section>

      {/* SSW Section */}
      <section className="flex flex-col items-center">
        {/* SSW Circle */}
        <SSWCircle
          currentSSW={currentSSW}
          dayInWeek={dayInWeek}
          babyNames={babyNames}
          title={sswContent?.title}
        />

        {/* Trimester Info */}
        <p className="mt-8 text-white/80 text-base">{t(`ssw:trimester.${trimester}`)}</p>

        {/* Loading State */}
        {isLoadingContent ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
            <span className="ml-2 text-white/70">{t('ssw:loading')}</span>
          </div>
        ) : (
          /* SSW Details */
          <div className="w-full space-y-6 mt-8">
            {/* Größe */}
            {sswContent?.baby_size_cm && (
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Ruler className="w-5 h-5 text-white" />
                    <span className="text-white text-sm">{t('ssw:size.label')}</span>
                  </div>
                  <span className="text-white/60 text-xs ml-7">{t('ssw:size.sublabel')}</span>
                </div>
                <div className="flex items-baseline">
                  <span className="text-white text-5xl font-bold">{Math.round(sswContent.baby_size_cm)}</span>
                  <span className="text-white text-xl ml-1">cm</span>
                </div>
              </div>
            )}

            {/* Gewicht */}
            {weightData && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Weight className="w-5 h-5 text-white" />
                  <span className="text-white text-sm">{t('ssw:weight.label')}</span>
                </div>
                <div className="flex items-baseline">
                  <span className="text-white text-5xl font-bold">{weightData.value}</span>
                  <span className="text-white text-xl ml-1">{weightData.unit}</span>
                </div>
              </div>
            )}

            {/* Tage bis ET */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-white" />
                <span className="text-white text-sm">{t('ssw:daysRemaining.label', { date: dueDate ? formatDate(dueDate) : '—' })}</span>
              </div>
              <div className="flex items-baseline">
                <span className="text-white text-5xl font-bold">{daysRemaining}</span>
                <span className="text-white text-xl ml-1">{t('ssw:daysRemaining.unit')}</span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Termine diese Woche Section */}
      <section className="mt-16">
        <h2 className="text-white text-2xl font-bold mb-4">{t('ssw:appointments.title')}</h2>

        <div className="space-y-5">
          {appointments.length > 0 ? (
            <>
              {appointments.map((apt) => {
                const aptDate = new Date(apt.appointment_date);
                const day = aptDate.toLocaleDateString(dateLocale, { day: 'numeric' });
                const month = aptDate.toLocaleDateString(dateLocale, { month: 'short' }).toUpperCase().replace('.', '');
                const weekday = aptDate.toLocaleDateString(dateLocale, { weekday: 'long' });
                const aptSubtitle = apt.appointment_time
                  ? t('ssw:appointments.weekdayWithTime', { weekday, time: apt.appointment_time })
                  : t('ssw:appointments.weekday', { weekday });

                return (
                  <Link key={apt.id} to="/calendar" className="appointment-link">
                    <div className="date-box">
                      <span className="date-day text-white">{day}</span>
                      <span className="date-month text-white">{month}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{apt.title}</p>
                      <p className="text-white/70 text-sm">{aptSubtitle}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-pp-orange" />
                  </Link>
                );
              })}
            </>
          ) : (
            <p className="text-white/70 text-sm">{t('ssw:appointments.none')}</p>
          )}

          {/* Alle anzeigen Button */}
          <Link to="/calendar" className="block mt-5">
            <button className="w-full py-3 bg-pp-orange rounded-pp flex items-center justify-center gap-2 text-white font-semibold text-base">
              {t('common:actions.showAll')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      {/* SSW Content Section */}
      {!isLoadingContent && (
        <section className="mt-16 space-y-8">
          {/* Entwicklung */}
          {babyDevelopment.length > 0 && (
            <div>
              <h3 className="text-white text-xl font-semibold mb-4">{t('ssw:content.development')}</h3>
              <div className="space-y-3">
                {babyDevelopment.map((item, index) => (
                  <div key={index} className="rounded-[15px] px-4 py-2 flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-pp-orange flex-shrink-0 mt-0.5" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Körper der Mutter */}
          {motherBody.length > 0 && (
            <div>
              <h3 className="text-white text-xl font-semibold mb-1">{bodyTitle}</h3>
              <p className="text-white/60 text-sm mb-4 italic">{t('ssw:content.bodySubtitle')}</p>
              <div className="space-y-3">
                {motherBody.map((item, index) => (
                  <div key={index} className="rounded-[15px] px-4 py-2 flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-pp-orange flex-shrink-0 mt-0.5" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tipps für die Woche */}
          {tips.length > 0 && (
            <div>
              <h3 className="text-white text-xl font-semibold mb-4">{t('ssw:content.tips')}</h3>
              <div className="space-y-3">
                {tips.map((tip, index) => (
                  <div key={index} className="rounded-[15px] px-4 py-2 flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-pp-orange flex-shrink-0 mt-0.5" />
                    <span className="text-white">{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product Recommendation (current position) */}
          <RecommendationSlot placement="ssw_content" position="before_text" />

          {/* Wochenübersicht (Multi-Section) */}
          {overviewSections.length > 0 && (
            <div>
              <h3 className="text-white text-xl font-semibold mb-4">{partnerTipsTitle}</h3>
              <div className="space-y-4">
                {overviewSections.map((section, index) => (
                  <div key={index}>
                    {section.title && index > 0 && (
                      <h4 className="text-white text-lg font-semibold mb-3">{section.title}</h4>
                    )}
                    <div className="rounded-[15px] px-4 py-1">
                      {/<[a-z][\s\S]*>/i.test(section.content) ? (
                        <div className="prose prose-sm prose-invert max-w-none prose-p:text-white/80 prose-p:leading-relaxed prose-p:my-2 prose-headings:text-white prose-strong:text-white prose-a:text-pp-teal prose-li:text-white/80">
                          {parse(section.content)}
                        </div>
                      ) : (
                        <p className="text-white">{section.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No content message */}
          {!sswContent && (
            <div className="rounded-[15px] p-6 text-center">
              <p className="text-white/60">{t('ssw:content.noContent', { week: currentSSW })}</p>
            </div>
          )}

          {/* Product Recommendation (end position) */}
          <RecommendationSlot placement="ssw_content" position="end" />
        </section>
      )}
    </>
  );
}

// Helper function to get start date of a specific SSW
function getSSWStartDate(dueDate: string, ssw: number): Date {
  const due = new Date(dueDate);
  const lmpDate = new Date(due.getTime() - 280 * 24 * 60 * 60 * 1000);
  const sswStart = new Date(lmpDate.getTime() + (ssw - 1) * 7 * 24 * 60 * 60 * 1000);
  return sswStart;
}
